// How a causa's PJUD verification state reads in the UI — the dot/pill color,
// the human label, and the explanatory subtext. Shared by the Causas table and
// CausaDetalle so both surfaces are aligned.
//
// `verificationStatus` is the VerificationStatus enum
// (CREATED | AWAITING_RESPONSE | VERIFIED_OK | NOT_EXIST | RESPONSE_ERROR;
// VERIFIED_FAIL is the deprecated predecessor of NOT_EXIST). CREATED and
// AWAITING_RESPONSE are internal scraper distinctions; to the user both read as
// "Sincronizando" (the dot breathes, same pulse as "Procesando pago").
// NOT_EXIST = the causa doesn't exist in PJUD (terminal). RESPONSE_ERROR = a
// scraper failure (retryable; the API auto-relaunches it).

const SYNCING = {
  dot: 'sync',
  label: 'Sincronizando',
  sub: 'Esperando respuesta del PJUD.',
  tone: 'warn',
};

const VERIFICATION = {
  CREATED: SYNCING,
  AWAITING_RESPONSE: SYNCING,
  VERIFIED_OK: {
    dot: 'ok',
    label: 'Suscrita',
    sub: 'Vigente y sincronizada.',
  },
  NOT_EXIST: {
    dot: 'err',
    label: 'No encontrada',
    sub: 'El PJUD no reconoce la causa.',
    tone: 'err',
  },
  // Deprecated predecessor of NOT_EXIST; kept so legacy rows still render.
  VERIFIED_FAIL: {
    dot: 'err',
    label: 'No encontrada',
    sub: 'El PJUD no reconoce la causa.',
    tone: 'err',
  },
  // Transient scraper failure that auto-retries — deliberately amber, NOT the
  // red/urgent of terminal NOT_EXIST. dot 'sync' keeps it out of the red row
  // accent (Causas urgent = dot==='err') and renders the amber "working on it"
  // pill; the label makes clear it's an error being retried, not plain syncing.
  RESPONSE_ERROR: {
    dot: 'sync',
    label: 'Error de verificación',
    sub: 'Hubo un problema al consultar el PJUD; se reintentará.',
    tone: 'warn',
  },
};

/**
 * Display model for a causa's verification state.
 * @param {string} verificationStatus
 * @returns {{dot: string, label: string, sub: string, tone?: string}} Unknown or
 *   nullish values fall back to the in-flight "Sincronizando" reading.
 */
export function causaVerificationDisplay(verificationStatus) {
  const key = (verificationStatus || '').toUpperCase();
  return VERIFICATION[key] ?? SYNCING;
}

// The states the scraper still moves on its own, with no user action: a view
// showing one of them is showing a value that changes underneath it, so it has to
// ask again. RESPONSE_ERROR belongs here because the API auto-relaunches it.
// Declared next to the state table so the enum lives in one file.
//
// A value outside the enum is deliberately excluded, though the display reads it
// as "Sincronizando": a payload that lost the field would otherwise poll for
// ever.
const IN_FLIGHT = new Set(['CREATED', 'AWAITING_RESPONSE', 'RESPONSE_ERROR']);

/**
 * Whether the causa's verification is still moving (PJUD hasn't settled it).
 * @param {string} verificationStatus
 * @returns {boolean}
 */
/**
 * Whether a causa admits a new diligencia.
 *
 * The API gates on the same fact: a causa the PJUD has not confirmed cannot
 * carry one, and trying answers a conflict. The web mirrors that gate rather
 * than restating it, so the reading lives here once and both the picker and the
 * detail read it.
 * @param {string|null|undefined} verificationStatus
 * @returns {boolean}
 */
export function admitsDiligencia(verificationStatus) {
  return String(verificationStatus || '').toUpperCase() === 'VERIFIED_OK';
}

// Whether a case still admits changing its assignee.
//
// The API answers PATCH /cases/:caseId/assignee with 409 caseNotEditable on
// NOT_EXIST and VERIFIED_FAIL: a case PJUD does not recognize leaves deleting
// it as the only action. The web hides the control on the same states, as
// admitsDiligencia does for a new diligence.
const NOT_EDITABLE = new Set(['NOT_EXIST', 'VERIFIED_FAIL']);
export function admitsAssignee(verificationStatus) {
  return !NOT_EDITABLE.has(String(verificationStatus || '').toUpperCase());
}

export function isCausaSyncing(verificationStatus) {
  return IN_FLIGHT.has((verificationStatus || '').toUpperCase());
}

/**
 * Builds the human-legible rol ("C-1234-2026") from a causa/case record. Rows
 * that already carry a fully-formed `rol` are returned as-is (no double prefix).
 * @param {{rol?: string, id?: string, bookType?: string, book?: string, year?: string|number}} [c]
 * @returns {string}
 */
export function buildRol(c) {
  if (!c) return '—';
  const rol = c.rol || c.id || '?';
  if (typeof rol === 'string' && rol.includes('-')) return rol;
  const book = c.bookType || c.book || '';
  const year = c.year || '';
  return `${book ? book + '-' : ''}${rol}${year ? '-' + year : ''}`;
}
